import { Vector3, type PerspectiveCamera } from 'three/webgpu';
import {
  rooms,
  walls,
  wallSegments,
  furniture,
  furnitureBounds,
  stair,
  roomFootprints,
  floorFootprints,
  type FloorConfig,
} from '../mansion/MansionLayout';
import { floorAtEyeHeight, roomAtPosition } from './FloorManager';

/** The same X/Z coordinates drive walls, furniture, routing and this SVG. */
export function mapPoint(x: number, z: number, floor: FloorConfig): [number, number] {
  const b = floor.worldBounds,
    m = floor.mapBounds;
  return [
    m.x + ((x - b.minX) / (b.maxX - b.minX)) * m.width,
    m.y + ((z - b.minZ) / (b.maxZ - b.minZ)) * m.height,
  ];
}
export class FloorPlan {
  private floor = '';
  private readonly direction = new Vector3();
  constructor(
    private readonly host: HTMLElement,
    private readonly navigate: (id: string) => void,
  ) {
    host.addEventListener('click', (event) => {
      const id = (event.target as Element).closest('[data-map-room]')?.getAttribute('data-map-room');
      if (id) this.navigate(rooms.find((r) => r.id === id)!.node);
    });
    host.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const id = (event.target as Element).closest('[data-map-room]')?.getAttribute('data-map-room');
      if (id) {
        event.preventDefault();
        this.navigate(rooms.find((r) => r.id === id)!.node);
      }
    });
  }
  private draw(f: FloorConfig): void {
    const point = (x: number, z: number) => mapPoint(x, z, f);
    const rect = (b: { minX: number; maxX: number; minZ: number; maxZ: number }) => {
      const [x, y] = point(b.minX, b.minZ),
        [x2, y2] = point(b.maxX, b.maxZ);
      return `x="${x}" y="${y}" width="${x2 - x}" height="${y2 - y}"`;
    };
    // Trace this floor's actual occupied outline, including the upper foyer void.
    // The single-story garage and connector belong only to the ground floor.
    const outline = (footprint: ReturnType<typeof floorFootprints>): string => {
      const xs = [...new Set(footprint.flatMap((b) => [b.minX, b.maxX]))].sort((a, b) => a - b);
      const zs = [...new Set(footprint.flatMap((b) => [b.minZ, b.maxZ]))].sort((a, b) => a - b);
      const occupied = (i: number, j: number) =>
        i >= 0 &&
        j >= 0 &&
        i < xs.length - 1 &&
        j < zs.length - 1 &&
        footprint.some(
          (b) =>
            (xs[i] + xs[i + 1]) / 2 > b.minX &&
            (xs[i] + xs[i + 1]) / 2 < b.maxX &&
            (zs[j] + zs[j + 1]) / 2 > b.minZ &&
            (zs[j] + zs[j + 1]) / 2 < b.maxZ,
        );
      const boundary: string[] = [];
      const line = (x1: number, z1: number, x2: number, z2: number) => {
        const a = point(x1, z1),
          b = point(x2, z2);
        boundary.push(`M${a[0]},${a[1]}L${b[0]},${b[1]}`);
      };
      for (let i = 0; i < xs.length - 1; i++)
        for (let j = 0; j < zs.length - 1; j++)
          if (occupied(i, j)) {
            if (!occupied(i - 1, j)) line(xs[i], zs[j], xs[i], zs[j + 1]);
            if (!occupied(i + 1, j)) line(xs[i + 1], zs[j], xs[i + 1], zs[j + 1]);
            if (!occupied(i, j - 1)) line(xs[i], zs[j], xs[i + 1], zs[j]);
            if (!occupied(i, j + 1)) line(xs[i], zs[j + 1], xs[i + 1], zs[j + 1]);
          }
      return boundary.join(' ');
    };
    const boundary = outline(floorFootprints(f.id, true));
    const foyer = rooms.find((r) => r.id === 'foyer')!.bounds,
      gallery = rooms.find((r) => r.id === 'upper-hall')!.bounds;
    const voidCenter = point((foyer.minX + foyer.maxX) / 2, (gallery.maxZ + foyer.maxZ) / 2);
    this.host.innerHTML = `<svg viewBox="0 0 320 198" aria-label="${f.label} plan. Select a room to visit.">
      ${f.id === 'upper' ? `<text x="${voidCenter[0]}" y="${voidCenter[1]}" class="map-label">Open to foyer</text>` : ''}
      ${rooms
        .filter((r) => r.floor === f.id)
        .map((r) => {
          const [x, y] = point((r.bounds.minX + r.bounds.maxX) / 2, (r.bounds.minZ + r.bounds.maxZ) / 2);
          const labels: Record<string, string> = {
            'stairs-ground': 'Stair',
            'stairs-upper': 'Stair',
            'ground-hall': 'Hall',
            'upper-hall': 'Gallery',
            powder: 'Powder',
            closet: 'Dressing',
            'bedroom-2': 'Bed 02',
            'bedroom-3': 'Bed 03',
            'bedroom-4': 'Bed 04',
            primary: 'Primary',
            bath: 'Bath',
            garage: 'Garage · 3 bays',
            'garage-entry': 'Entry',
          };
          const labelTransform = r.id === 'garage-entry' ? ` transform="rotate(-90 ${x} ${y})"` : '';
          return `<g data-map-room="${r.id}" role="button" tabindex="0" aria-label="Visit ${r.label}">${roomFootprints(
            r,
          )
            .map((b) => `<rect ${rect(b)} class="map-room" style="stroke:none"/>`)
            .join(
              '',
            )}<path d="${outline(roomFootprints(r))}" class="map-room" style="fill:none;pointer-events:none"/><text x="${x}" y="${y}"${labelTransform} class="map-label">${labels[r.id] ?? r.label}</text></g>`;
        })
        .join('')}
      <path d="${boundary}" class="map-outline" style="fill:none;pointer-events:none"/>
      <g class="map-furniture" aria-hidden="true">${furniture
        .filter((i) => rooms.find((r) => r.id === i.room)?.floor === f.id)
        .map((i) => `<rect ${rect(furnitureBounds(i))}/>`)
        .join('')}</g>
      <g class="map-walls" aria-hidden="true">${walls
        .filter((w) => w.floor === f.id)
        .flatMap((w) => wallSegments(w).filter((s) => !s.top))
        .map((s) => {
          const a = point(...s.a),
            b = point(...s.b);
          return `<path d="M${a[0]},${a[1]}L${b[0]},${b[1]}"/>`;
        })
        .join('')}</g>
      <g class="map-stair" aria-hidden="true">${Array.from({ length: stair.steps }, (_, i) => {
        const z = stair.front - (i * (stair.front - stair.rear)) / stair.steps;
        return [stair.lowerX, stair.upperX]
          .map((center) => {
            const [x, y] = point(center - stair.width / 2, z),
              [x2] = point(center + stair.width / 2, z);
            return `<path d="M${x},${y}H${x2}"/>`;
          })
          .join('');
      }).join('')}</g>
      <g data-map-marker aria-label="Your location and viewing direction"><path class="map-cone" d="M0,0 L-11,-25 Q0,-32 11,-25Z"/><path class="map-arrow" d="M0,-11 L5,4 L0,1 L-5,4Z"/><circle r="3"/></g>
      <text x="160" y="192" class="map-caption">FRONT ARRIVAL</text></svg>`;
    this.floor = f.id;
  }
  update(camera: PerspectiveCamera): void {
    const f = floorAtEyeHeight(camera.position.y);
    if (this.floor !== f.id) this.draw(f);
    const [x, y] = mapPoint(camera.position.x, camera.position.z, f);
    camera.getWorldDirection(this.direction);
    const sx = f.mapBounds.width / (f.worldBounds.maxX - f.worldBounds.minX),
      sz = f.mapBounds.height / (f.worldBounds.maxZ - f.worldBounds.minZ);
    const angle = (Math.atan2(this.direction.x * sx, -this.direction.z * sz) * 180) / Math.PI;
    const marker = this.host.querySelector<SVGGElement>('[data-map-marker]')!;
    marker.setAttribute('transform', `translate(${x} ${y}) rotate(${angle})`);
    marker.dataset.worldX = String(camera.position.x);
    marker.dataset.worldZ = String(camera.position.z);
    marker.dataset.angle = String(angle);
    const room = roomAtPosition(camera.position.x, camera.position.y, camera.position.z);
    this.host.dataset.floor = f.id;
    this.host
      .querySelectorAll('[data-map-room]')
      .forEach((e) => e.setAttribute('aria-current', String(e.getAttribute('data-map-room') === room?.id)));
  }
}
